import { Box, Button, Grid, Modal, Stack, Typography } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import MeasurementPoints from "../MeasurementPoints";
import EnterContractModal from "components/Modals/EnterContract";
import EditContractModal from "components/Modals/EditContract";
import LegalConsentModal from "./LegalConsentModal";
import React from "react";
import { GridRowId } from "@mui/x-data-grid";
import { Client } from "api/models/Client";
import { MeteringPoint } from "api/models/MeteringPoint";
import { Snackbar } from "types/snackbar";

interface MeasurementGridProps {
  rowSelectionModel: GridRowId[];
  setRowSelectionModel: React.Dispatch<React.SetStateAction<GridRowId[]>>;
  client: Client;
  loading: boolean;
  handleLoadMeasurementData: (
    loadAll?: boolean,
    legalConsent?: boolean,
  ) => void;
  handleLoadMeteringPoints: (legalConsent?: boolean) => void;
  setLoadMeteringPointsModal: React.Dispatch<React.SetStateAction<boolean>>;
  loadMeteringPointsModal: boolean;
  setFetchReadingsModal: React.Dispatch<React.SetStateAction<boolean>>;
  fetchReadingsModal: boolean;
  setEditContractModal: React.Dispatch<React.SetStateAction<boolean>>;
  setEnterContractModal: React.Dispatch<React.SetStateAction<boolean>>;
  meteringPoints: {
    totalElements: number;
    totalPages: number;
    size: number;
    content: MeteringPoint[];
  };
  clientId: string;
  paginationModel: { page: number; pageSize: number };
  setPaginationModel: React.Dispatch<React.SetStateAction<any>>;
  enterContractModal: boolean;
  editContractModal: boolean;
  disabled?: boolean;
  setSnackbar: React.Dispatch<React.SetStateAction<Snackbar>>;
}

const MeasurementGrid: React.FC<MeasurementGridProps> = ({
  rowSelectionModel,
  setRowSelectionModel,
  client,
  loading,
  handleLoadMeasurementData,
  handleLoadMeteringPoints,
  setLoadMeteringPointsModal,
  loadMeteringPointsModal,
  setFetchReadingsModal,
  fetchReadingsModal,
  setEditContractModal,
  setEnterContractModal,
  meteringPoints,
  clientId,
  paginationModel,
  setPaginationModel,
  enterContractModal,
  editContractModal,
  disabled,
  setSnackbar,
}) => {
  return (
    <Grid container columnSpacing={8} sx={{ mb: "2rem" }}>
      <Grid item sm={12}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Stack direction="column">
            <Typography variant="h5" component="h5">
              Metering points
            </Typography>
            {rowSelectionModel.length > 0 && (
              <Typography
                sx={{ mt: "0.2rem" }}
              >{`${rowSelectionModel.length} out of ${client.meteringPointsCount} metering points selected`}</Typography>
            )}
          </Stack>

          {!disabled && (
            <Stack direction="row" spacing={2}>
              {rowSelectionModel.length === 0 && (
                <LoadingButton
                  size="small"
                  onClick={() => setLoadMeteringPointsModal(true)}
                  variant={
                    client.meteringPointsCount > 0 ? "outlined" : "contained"
                  }
                  loading={loading}
                  loadingIndicator="Loading…"
                  disabled={loading}
                >
                  Load metering points
                </LoadingButton>
              )}
              {client.meteringPointsCount > 0 &&
                rowSelectionModel.length === 0 && (
                  <Button
                    size="small"
                    variant={
                      client.meteringPointsCount > 0 ? "contained" : "outlined"
                    }
                    onClick={() => {
                      setFetchReadingsModal(true);
                    }}
                  >
                    Fetch all measurement data
                  </Button>
                )}
              {client.meteringPointsCount > 0 &&
                rowSelectionModel.length > 0 && (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => setFetchReadingsModal(true)}
                  >
                    Fetch measurement data
                  </Button>
                )}
              {rowSelectionModel.length > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setEditContractModal(true)}
                >
                  Edit contract end date ({rowSelectionModel.length})
                </Button>
              )}
              {rowSelectionModel.length > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setEnterContractModal(true)}
                >
                  Enter contract ({rowSelectionModel.length})
                </Button>
              )}
            </Stack>
          )}
        </Box>
        {meteringPoints?.content.length > 0 ? (
          <MeasurementPoints
            clientId={clientId}
            meteringPoints={meteringPoints}
            rowSelectionModel={rowSelectionModel}
            setRowSelectionModel={setRowSelectionModel}
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
            setSnackbar={setSnackbar}
          />
        ) : (
          <Typography variant="subtitle1" sx={{ mt: "2rem" }}>
            No metering points.
            <br />
            Click "Load metering points" to fetch metering points from Elering
            Datahub.
          </Typography>
        )}
      </Grid>
      <Modal
        open={enterContractModal}
        onClose={() => setEnterContractModal(false)}
      >
        <Box>
          <EnterContractModal
            client={client}
            selectedMeteringPoints={rowSelectionModel}
            setSelectedMeteringPoints={setRowSelectionModel}
            setOpenModal={setEnterContractModal}
            setSnackbar={setSnackbar}
          />
        </Box>
      </Modal>
      <Modal
        open={editContractModal}
        onClose={() => {
          setEditContractModal(false);
          handleLoadMeteringPoints();
        }}
      >
        <Box>
          <EditContractModal
            selectedMeteringPoints={rowSelectionModel}
            setSelectedMeteringPoints={setRowSelectionModel}
            setOpenModal={setEditContractModal}
            setSnackbar={setSnackbar}
          />
        </Box>
      </Modal>
      <Modal
        open={loadMeteringPointsModal}
        onClose={() => setLoadMeteringPointsModal(false)}
      >
        <Box>
          <LegalConsentModal
            contractType={client.contractType}
            title="Load metering points"
            buttonTitle="Load metering points"
            action={handleLoadMeteringPoints}
            setModal={setLoadMeteringPointsModal}
          />
        </Box>
      </Modal>
      <Modal
        open={fetchReadingsModal}
        onClose={() => setFetchReadingsModal(false)}
      >
        <Box>
          <LegalConsentModal
            contractType={client.contractType}
            title="Fetch measurement data"
            buttonTitle="Fetch measurement data"
            action={handleLoadMeasurementData}
            type="measurementData"
            fetchAll={
              client.meteringPointsCount > 0 && rowSelectionModel.length === 0
            }
            setModal={setFetchReadingsModal}
          />
        </Box>
      </Modal>
    </Grid>
  );
};

export default MeasurementGrid;
